import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const chords = location.state?.chords || [];
  const tempo = location.state?.tempo;
  const key = location.state?.key;
  const audioURL = location.state?.audioURL;
  const fileName = location.state?.fileName;

  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true); // 🔑 toggle state

  // Keep refs for all chord elements
  const chordRefs = useRef([]);

  // Sync playback time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    audio.addEventListener("timeupdate", updateTime);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
    };
  }, []);

  // Determine active chord
  const getActiveIndex = () => {
    let activeIndex = -1;
    chords.forEach((chord, i) => {
      const [m, s] = chord.time.split(":").map(Number);
      const chordTime = m * 60 + s;
      if (currentTime >= chordTime) {
        activeIndex = i;
      }
    });
    return activeIndex;
  };

  const activeIndex = getActiveIndex();

  // Auto-scroll active chord into view
  useEffect(() => {
    if (
      autoScroll &&
      activeIndex !== -1 &&
      chordRefs.current[activeIndex]
    ) {
      chordRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [activeIndex, autoScroll]);

  return (
    <section className="pt-24 mt-12 flex flex-col items-center justify-center px-6 text-white text-center pb-32">
      
      {/* Audio Player */}
      {audioURL && (
        <div className="mb-6 w-full max-w-xl">
          {fileName && (
            <p className="text-gray-400 mb-2 text-sm">
              Now analyzing: <span className="font-semibold text-white">{fileName}</span>
            </p>
          )}
          <audio ref={audioRef} controls src={audioURL} className="w-full rounded shadow-md" />
        </div>
      )}
      
      {/* Key & Tempo badges */}
      <div className="mb-6 flex flex-wrap gap-3 justify-center">
        {key && (
          <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm">
            <span className="mr-2">Key</span>
            <span className="font-semibold">{key}</span>
          </span>
        )}

        {typeof tempo === "number" && (
          <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm">
            <span className="mr-2">Tempo</span>
            <span className="font-semibold">{tempo} BPM</span>
          </span>
        )}

        {/* Auto-scroll toggle */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={() => setAutoScroll(!autoScroll)}
            className="accent-indigo-500"
          />
          Follow playback
        </label>
      </div>

      <h2 className="text-3xl font-bold mb-6">Chords</h2>

      <div className="grid grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto w-full">
        {chords.map((chord, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={index}
              ref={(el) => (chordRefs.current[index] = el)}
              className={`rounded-md px-4 py-6 flex flex-col items-center justify-center shadow-md transition 
                ${isActive ? "bg-indigo-500 text-white scale-105" : "bg-white/10 text-gray-300"}`}
            >
              <span className="text-lg font-semibold">{chord.chord}</span>
              <span className="text-sm mt-2">{chord.time}</span>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => navigate("/")}
        className="mt-6 px-4 py-2 bg-indigo-500 rounded hover:bg-indigo-600"
      >
        Try Another Song
      </button>
    </section>
  );
}

export default Results;
